/**
 * Subscription Page — payment success/failed modals, simplified embed code
 * გამოწერის გვერდი
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button, Modal, Spinner, useNotification } from '@aiia/portal-core';
import {
  useCurrentUser,
  useSubscription,
  usePlans,
  useUsage,
  useInvoices,
  useUpgradePlan,
  useCancelSubscription,
  usePayInvoice,
  useCancelUpgrade,
} from '../hooks';
import type { Plan, BillingCycle, Invoice } from '../features/api/subscription';
import { downloadInvoicePdf } from '../features/api/subscription';
import { getApiErrorMessage } from '../utils/errorMessages';
import { formatDate } from '../utils/formatDate';

export default function SubscriptionPage() {
  const { t } = useTranslation();
  const notification = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useCurrentUser();
  const userId = user?.id || '';

  const { subscription, isLoading: loadingSubscription, refetch: refetchSubscription } = useSubscription(userId);
  const { plans, isLoading: loadingPlans } = usePlans(userId);
  const { usage, isLoading: loadingUsage } = useUsage(userId);
  const { invoices, isLoading: loadingInvoices, refetch: refetchInvoices } = useInvoices(userId);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      setShowPaymentSuccessModal(true);
      refetchSubscription();
      refetchInvoices();
      setSearchParams({}, { replace: true });
    } else if (status === 'failed') {
      setShowPaymentFailedModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, notification, t, refetchSubscription, refetchInvoices, setSearchParams]);

  const upgradePlan = useUpgradePlan();
  const cancelSubscription = useCancelSubscription(userId);
  const payInvoice = usePayInvoice(userId);
  const cancelUpgradeMutation = useCancelUpgrade();

  const [billingCycle, setBillingCycle] = useState<BillingCycle>('MONTHLY');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingInvoiceId, setCancellingInvoiceId] = useState<string | null>(null);
  const [pendingPaymentInvoiceId, setPendingPaymentInvoiceId] = useState<string | null>(null);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState<string | null>(null);

  const isValidPaymentUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSelectPlan = async (plan: Plan) => {
    if (subscription?.planCode === plan.code) return;

    try {
      const response = await upgradePlan.mutateAsync({
        planCode: plan.code,
        billingCycle,
      });

      if (response.paymentUrl) {
        if (!isValidPaymentUrl(response.paymentUrl)) {
          notification.error(t('subscription.invalidPaymentUrl'));
          return;
        }
        window.location.href = response.paymentUrl;
      } else {
        notification.success(t('subscription.planChanged'));
        refetchSubscription();
      }
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription.mutateAsync({ reason: cancelReason });
      notification.success(t('subscription.subscriptionCancelled'));
      setShowCancelModal(false);
      setCancelReason('');
      refetchSubscription();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handlePayInvoice = async (invoiceId: string) => {
    try {
      const response = await payInvoice.mutateAsync(invoiceId);
      if (response.paymentUrl) {
        if (!isValidPaymentUrl(response.paymentUrl)) {
          notification.error(t('subscription.invalidPaymentUrl'));
          return;
        }
        setPendingPaymentInvoiceId(invoiceId);
        setPendingPaymentUrl(response.paymentUrl);
      }
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleCancelInvoice = async (invoiceId: string) => {
    setCancellingInvoiceId(invoiceId);
    try {
      await cancelUpgradeMutation.mutateAsync(invoiceId);
      notification.success(t('subscription.invoiceCancelled'));
      refetchInvoices();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    } finally {
      setCancellingInvoiceId(null);
    }
  };

  const handleConfirmPayment = () => {
    if (pendingPaymentUrl) {
      window.location.href = pendingPaymentUrl;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'TRIAL': return 'bg-blue-100 text-blue-800';
      case 'CANCELED': return 'bg-red-100 text-red-800';
      case 'EXPIRED': return 'bg-gray-100 text-gray-800';
      case 'PAST_DUE': return 'bg-yellow-100 text-yellow-800';
      case 'PAID': return 'bg-green-100 text-green-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loadingSubscription || loadingPlans) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('subscription.title')}</h1>
        <p className="text-gray-600 mt-1">{t('subscription.subtitle')}</p>
      </div>

      {/* Current Subscription */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscription.currentSubscription')}</h2>

        {subscription ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <p className="text-xl font-bold text-gray-900">{subscription.planName}</p>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
                  {subscription.status}
                </span>
              </div>
              <p className="text-gray-500 mt-1">
                {subscription.billingCycle === 'MONTHLY' ? t('subscription.monthlyBilling') : t('subscription.annualBilling')}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {subscription.status === 'TRIAL' ? (
                  <>{t('subscription.trialEnds', { date: formatDate(subscription.trialEndsAt) })}</>
                ) : (
                  <>{t('subscription.currentPeriod', { start: formatDate(subscription.currentPeriodStart), end: formatDate(subscription.currentPeriodEnd) })}</>
                )}
              </p>
            </div>
            {subscription.status !== 'CANCELED' && subscription.status !== 'EXPIRED' && (
              <Button
                variant="secondary"
                onClick={() => setShowCancelModal(true)}
              >
                {t('subscription.cancelSubscription')}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-gray-500">{t('subscription.noSubscription')}</p>
        )}
      </Card>

      {/* Usage Stats */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscription.usageThisPeriod')}</h2>

        {loadingUsage ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : usage ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <UsageStat
              label={t('subscription.conversations')}
              used={usage.conversationsUsed}
              limit={usage.conversationsLimit}
            />
            <UsageStat
              label={t('subscription.apiCalls')}
              used={usage.apiCallsUsed}
              limit={usage.apiCallsLimit}
            />
            <UsageStat
              label={t('subscription.widgetsUsage')}
              used={usage.widgetsUsed}
              limit={usage.widgetsLimit}
            />
            <UsageStat
              label={t('subscription.apiKeysUsage')}
              used={usage.apiKeysUsed}
              limit={usage.apiKeysLimit}
            />
            <UsageStat
              label={t('subscription.dataFilesUsage')}
              used={usage.dataFilesUsed}
              limit={usage.dataFilesLimit}
            />
          </div>
        ) : (
          <p className="text-gray-500">{t('subscription.noUsageData')}</p>
        )}
      </Card>

      {/* Plan Selection */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('subscription.availablePlans')}</h2>

          {/* Billing Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'MONTHLY'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setBillingCycle('MONTHLY')}
            >
              {t('subscription.monthly')}
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'YEARLY'
                  ? 'bg-white text-gray-900 shadow'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setBillingCycle('YEARLY')}
            >
              {t('subscription.yearly')}
              <span className="ml-1 text-green-600 text-xs">{t('subscription.save20')}</span>
            </button>
          </div>
        </div>

        {plans && plans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                billingCycle={billingCycle}
                isCurrentPlan={subscription?.planCode === plan.code}
                currentPlanCode={subscription?.planCode}
                subscriptionStatus={subscription?.status}
                onSelect={() => handleSelectPlan(plan)}
                isLoading={upgradePlan.isPending}
                t={t}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">{t('subscription.noPlans')}</p>
        )}
      </Card>

      {/* Billing History */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('subscription.billingHistory')}</h2>

        {loadingInvoices ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : invoices && invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscription.invoice')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscription.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscription.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    userId={userId}
                    onPay={() => handlePayInvoice(invoice.id)}
                    isPaymentLoading={payInvoice.isPending}
                    onCancel={() => handleCancelInvoice(invoice.id)}
                    isCancelLoading={cancellingInvoiceId === invoice.id}
                    getStatusColor={getStatusColor}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">{t('subscription.noBillingHistory')}</p>
        )}
      </Card>

      {/* Payment Success Modal */}
      <Modal
        isOpen={showPaymentSuccessModal}
        onClose={() => setShowPaymentSuccessModal(false)}
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center py-4">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('subscription.paymentSuccess')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('subscription.paymentSuccessDesc')}
          </p>
          <Button onClick={() => setShowPaymentSuccessModal(false)} className="w-full">
            {t('common.ok') || 'OK'}
          </Button>
        </div>
      </Modal>

      {/* Payment Failed Modal */}
      <Modal
        isOpen={showPaymentFailedModal}
        onClose={() => setShowPaymentFailedModal(false)}
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center py-4">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('subscription.paymentFailed')}
          </h3>
          <p className="text-gray-600 mb-6">
            {t('subscription.paymentFailedDesc')}
          </p>
          <Button onClick={() => setShowPaymentFailedModal(false)} className="w-full">
            {t('common.ok') || 'OK'}
          </Button>
        </div>
      </Modal>

      {/* Payment Confirmation Modal */}
      {pendingPaymentInvoiceId && pendingPaymentUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('subscription.confirmPayment')}</h3>
            <p className="text-gray-600 mb-4">{t('subscription.paymentRedirectInfo')}</p>
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-3">{t('subscription.acceptedCards')}</p>
              <div className="flex items-center gap-3">
                {/* Visa logo */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 30" className="h-8 w-auto" aria-label="Visa">
                  <rect width="48" height="30" rx="4" fill="#1A1F71"/>
                  <text x="24" y="21" textAnchor="middle" fill="white" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="bold" fontStyle="italic" letterSpacing="1">VISA</text>
                </svg>
                {/* Mastercard logo */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 30" className="h-8 w-auto" aria-label="Mastercard">
                  <rect width="48" height="30" rx="4" fill="#252525"/>
                  <circle cx="18" cy="15" r="9" fill="#EB001B"/>
                  <circle cx="30" cy="15" r="9" fill="#F79E1B"/>
                  <path d="M24 7.8a9 9 0 0 1 0 14.4A9 9 0 0 1 24 7.8z" fill="#FF5F00"/>
                </svg>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setPendingPaymentInvoiceId(null);
                  setPendingPaymentUrl(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleConfirmPayment}>
                {t('subscription.confirmPayment')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('subscription.cancelTitle')}</h3>
            <p className="text-gray-600 mb-4">
              {t('subscription.cancelConfirm')}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('subscription.cancelReason')}
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder={t('subscription.cancelReasonPlaceholder')}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
              >
                {t('subscription.keepSubscription')}
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelSubscription}
                disabled={cancelSubscription.isPending}
              >
                {cancelSubscription.isPending ? t('common.cancelling') : t('subscription.cancelSubscription')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Usage Stat Component
function UsageStat({ label, used, limit }: { label: string; used: number; limit: number }) {
  const percentage = limit === -1 ? 0 : (used / limit) * 100;
  const isUnlimited = limit === -1;
  const isNearLimit = !isUnlimited && percentage >= 80;

  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-xl font-bold ${isNearLimit ? 'text-orange-600' : 'text-gray-900'}`}>
        {used} / {isUnlimited ? '∞' : limit}
      </p>
      {!isUnlimited && (
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${
              percentage >= 100 ? 'bg-red-500' :
              percentage >= 80 ? 'bg-orange-500' : 'bg-blue-500'
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

// Plan Card Component
const PLAN_ORDER: Record<string, number> = { TRIAL: 0, STARTER: 1, BUSINESS: 2, ENTERPRISE: 3 };

function PlanCard({
  plan,
  billingCycle,
  isCurrentPlan,
  currentPlanCode,
  subscriptionStatus,
  onSelect,
  isLoading,
  t,
}: {
  plan: Plan;
  billingCycle: BillingCycle;
  isCurrentPlan: boolean;
  currentPlanCode?: string;
  subscriptionStatus?: string;
  onSelect: () => void;
  isLoading: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const price = billingCycle === 'MONTHLY' ? plan.monthlyPrice : plan.yearlyPrice;
  const monthlyPrice = billingCycle === 'YEARLY' ? plan.yearlyPrice / 12 : plan.monthlyPrice;
  const isPopular = plan.isPopular || plan.recommended;
  const isEnterprise = plan.code === 'ENTERPRISE';

  const isTerminal = subscriptionStatus === 'CANCELED' || subscriptionStatus === 'EXPIRED';
  const currentOrder = PLAN_ORDER[currentPlanCode || ''] ?? -1;
  const planOrder = PLAN_ORDER[plan.code] ?? 0;
  // Canceled/expired users can select any plan; active users can only upgrade
  const canSelect = isTerminal ? !isCurrentPlan : planOrder > currentOrder;

  return (
    <div
      className={`relative border rounded-lg p-6 ${
        isPopular
          ? 'border-blue-500 shadow-lg'
          : isCurrentPlan
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
            {t('subscription.mostPopular')}
          </span>
        </div>
      )}

      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
            {t('subscription.currentPlan')}
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
        {plan.description && (
          <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
        )}
        <div className="mt-4">
          <span className="text-4xl font-bold text-gray-900">
            {isEnterprise ? t('subscription.customPricing') : price === 0 ? t('subscription.free') : `${plan.currency}${monthlyPrice.toFixed(0)}`}
          </span>
          {!isEnterprise && price > 0 && (
            <span className="text-gray-500">{t('subscription.perMonth')}</span>
          )}
        </div>
        {!isEnterprise && billingCycle === 'YEARLY' && price > 0 && (
          <p className="text-sm text-gray-400 mt-1">
            {t('subscription.billedYearly', { currency: plan.currency, price })}
          </p>
        )}
      </div>

      <ul className="space-y-3 mb-6">
        {plan.features
          .filter((feature) => {
            const val = Number(feature.value);
            const isBool = !feature.unlimited && (val === 0 || val === 1)
              && !feature.code.startsWith('MAX_');
            // Hide disabled boolean features (e.g. CUSTOM_BRANDING=0 on Starter)
            return !(isBool && val === 0);
          })
          .map((feature) => {
            const val = Number(feature.value);
            const isBooleanFeature = !feature.unlimited && (val === 0 || val === 1)
              && !feature.code.startsWith('MAX_');
            return (
              <li key={feature.code} className="flex items-center gap-2 text-sm">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-600">
                  {isBooleanFeature
                    ? feature.name
                    : <>{feature.name}: <span className="font-medium">{feature.unlimited ? '∞' : feature.displayValue || String(feature.value)}</span></>}
                </span>
              </li>
            );
          })}
      </ul>

      {isEnterprise ? (
        <div className="text-center">
          <Button
            className="w-full"
            variant="secondary"
            disabled
          >
            {t('subscription.contactUs')}
          </Button>
          <p className="text-xs text-gray-500 mt-2">{t('subscription.contactEnterprise')}</p>
        </div>
      ) : (
        <Button
          className="w-full"
          variant={isCurrentPlan ? 'secondary' : canSelect && isPopular ? 'primary' : 'secondary'}
          onClick={onSelect}
          disabled={!canSelect || isLoading}
        >
          {isCurrentPlan ? t('subscription.currentPlan') : t('subscription.selectPlan')}
        </Button>
      )}
    </div>
  );
}

// Invoice Row Component
function InvoiceRow({
  invoice,
  userId,
  onPay,
  isPaymentLoading,
  onCancel,
  isCancelLoading,
  getStatusColor,
  t,
}: {
  invoice: Invoice;
  userId: string;
  onPay: () => void;
  isPaymentLoading: boolean;
  onCancel: () => void;
  isCancelLoading: boolean;
  getStatusColor: (status: string) => string;
  t: (key: string) => string;
}) {
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadInvoicePdf(userId, invoice.id, invoice.invoiceNumber || invoice.id);
    } catch (error) {
      console.error('PDF download failed:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm font-medium text-gray-900">{invoice.invoiceNumber || invoice.id || '-'}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(invoice.createdAt)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        {invoice.currency || ''}{(invoice.total ?? 0).toFixed(2)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status || '')}`}>
          {invoice.status || '-'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
        {invoice.status === 'PENDING' && (
          <>
            <Button
              variant="primary"
              onClick={onPay}
              disabled={isPaymentLoading}
            >
              {t('subscription.payWithCard')}
            </Button>
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={isCancelLoading}
            >
              {isCancelLoading ? t('common.cancelling') : t('subscription.cancelInvoice')}
            </Button>
          </>
        )}
        {invoice.status === 'PAID' && (
          <button
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium disabled:opacity-50"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
          >
            {pdfLoading ? '...' : 'PDF'}
          </button>
        )}
      </td>
    </tr>
  );
}
